import { createContext, useCallback, useContext, useState } from 'react';

import { useLocalStorageState } from '@/services/localStorage';
import useDebouncedState from '@/utils/useDebouncedState';

type FiltersState = {
  page: {
    page: number;
    setPage: (page: number) => void;
  };
  search: {
    search: string;
    debouncedSearch: string;
    debouncedSetSearch: (search: string) => void;
  };
  roles: {
    roles: { id: string; name: string }[];
    setRoles: (roles: { id: string; name: string }[]) => void;
  };
  entities: {
    entities: { id: string; name: string }[];
    setEntities: (entities: { id: string; name: string }[]) => void;
  };
  activeFiltersCount: number;
  clearFilters: () => void;
};

export const FiltersStateContext = createContext<FiltersState>({} as FiltersState);

export const useFiltersState = (): FiltersState => {
  const [page, setPage] = useState(1);
  const resetPage = useCallback(() => setPage(1), [setPage]);

  const [search, debouncedSearch, debouncedSetSearch] = useDebouncedState('', 300, resetPage);

  const [roles, setRoles] = useLocalStorageState<{ id: string; name: string }[]>(
    'settings:organization:members:invitations:filters:roles',
    []
  );

  const [entities, setEntities] = useLocalStorageState<{ id: string; name: string }[]>(
    'settings:organization:members:invitations:filters:entities',
    []
  );

  return {
    page: {
      page,
      setPage,
    },
    search: {
      search,
      debouncedSearch,
      debouncedSetSearch,
    },
    roles: {
      roles,
      setRoles: (nextRoles: { id: string; name: string }[]) => {
        setRoles(nextRoles);
        resetPage();
      },
    },
    entities: {
      entities,
      setEntities: (nextEntities: { id: string; name: string }[]) => {
        setEntities(nextEntities);
        resetPage();
      },
    },
    activeFiltersCount: roles.length + entities.length,
    clearFilters: () => {
      setRoles([]);
      setEntities([]);
      resetPage();
    },
  };
};

export const useFilters = () => {
  return useContext(FiltersStateContext);
};
